import React, { useState } from 'react';
import { apiService } from '../../../../services/api';
import { CreateProdutoData, Produto } from '../../../../types/produtos';
import { useUnidadesMedida } from '../../../../hooks/useUnidadesMedida';
import Select from 'react-select';
import { toast } from 'react-toastify';
import ProdutosRelacionadosModal from '../../../../components/ProdutosRelacionadosModal';
import { searchProdutosRelacionados } from '../../../../utils/produtoSearch';

interface QuickProductFormProps {
  onSuccess: (newProduct: any) => void;
  onCancel: () => void;
  produtos: Produto[];
  onAddExisting: (produto: Produto) => void;
}

const QuickProductForm: React.FC<QuickProductFormProps> = ({
  onSuccess,
  onCancel,
  produtos,
  onAddExisting,
}) => {
  const { unidadesMedida } = useUnidadesMedida();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRelacionadosModal, setShowRelacionadosModal] = useState(false);
  const [produtosRelacionados, setProdutosRelacionados] = useState<Produto[]>([]);
  const [newProduto, setNewProduto] = useState<CreateProdutoData>({
    nome: '',
    descricao: '',
    marca: '',
    estoque: 0,
    preco_padrao: 0,
    unidade_medida: 'unidade'
  });

  const unidadeOptions = unidadesMedida.map(u => ({
    value: u.codigo,
    label: `${u.nome} (${u.codigo})`
  }));

  const handleInputChange = (field: keyof CreateProdutoData, value: any) => {
    setNewProduto(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNomeBlur = () => {
    if (!newProduto.nome.trim()) return;
    const relacionados = searchProdutosRelacionados(produtos, newProduto.nome);
    if (relacionados.length > 0) {
      setProdutosRelacionados(relacionados);
      setShowRelacionadosModal(true);
    }
  };

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!newProduto.nome.trim()) {
      toast.error('O nome do produto é obrigatório');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await apiService.createProduto(newProduto);

      if (response && response.data) {
        onSuccess(response.data);
      }
    } catch (error: any) {
      console.error('Erro ao criar produto:', error);
      toast.error(error.message || 'Erro ao criar produto');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <div className="quick-status-form-wrapper">
      <div className="alert alert-info d-flex align-items-center mb-3">
        <i className="ti ti-info-circle me-2"></i>
        <span>Cadastro rápido - Adicione um novo produto</span>
      </div>

      <div className="row">
        {/* Nome do Produto */}
        <div className="col-12 mb-3">
          <label className="form-label">
            Nome do Produto <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            value={newProduto.nome}
            onChange={(e) => handleInputChange('nome', e.target.value)}
            onBlur={handleNomeBlur}
            placeholder="Ex: Papel A4, Café 500g"
            autoFocus
          />
        </div>

        {/* Marca */}
        <div className="col-md-6 mb-3">
          <label className="form-label">Marca</label>
          <input
            type="text"
            className="form-control"
            value={newProduto.marca}
            onChange={(e) => handleInputChange('marca', e.target.value)}
            placeholder="Ex: HP, Chamex"
          />
        </div>

        {/* Preço Padrão */}
        <div className="col-md-6 mb-3">
          <label className="form-label">Preço Padrão (Ref.)</label>
          <div className="input-group">
            <span className="input-group-text">R$</span>
            <input
              type="number"
              className="form-control"
              value={newProduto.preco_padrao}
              onChange={(e) => handleInputChange('preco_padrao', parseFloat(e.target.value) || 0)}
              placeholder="0,00"
              step="0.01"
            />
          </div>
        </div>

        {/* Unidade de Medida */}
        <div className="col-12 mb-3">
          <label className="form-label">Unidade de Medida</label>
          <Select
            className="select2"
            options={unidadeOptions}
            value={unidadeOptions.find(opt => opt.value === newProduto.unidade_medida) || { value: 'unidade', label: 'Unidade (unidade)' }}
            onChange={(option) => handleInputChange('unidade_medida', option?.value || 'unidade')}
            placeholder="Selecione"
            classNamePrefix="react-select"
          />
        </div>

        {/* Estoque Inicial */}
        <div className="col-12 mb-3">
          <label className="form-label">Estoque Inicial</label>
          <input
            type="number"
            className="form-control"
            value={newProduto.estoque}
            onChange={(e) => handleInputChange('estoque', parseFloat(e.target.value) || 0)}
            placeholder="0"
          />
        </div>

        {/* Botões de Ação */}
        <div className="col-12">
          <div className="d-flex gap-2 justify-content-end pt-2 border-top">
            <button
              type="button"
              className="btn btn-light"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              <i className="ti ti-x me-1"></i>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Salvando...
                </>
              ) : (
                <>
                  <i className="ti ti-check me-1"></i>
                  Salvar e Adicionar
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>

    {showRelacionadosModal && (
      <ProdutosRelacionadosModal
        searchTerm={newProduto.nome}
        produtos={produtosRelacionados}
        primaryAction={{
          label: 'Adicionar',
          className: 'btn-primary',
          onClick: (produto) => {
            setShowRelacionadosModal(false);
            onAddExisting(produto);
          },
        }}
        onContinue={() => setShowRelacionadosModal(false)}
        onClose={() => setShowRelacionadosModal(false)}
      />
    )}
    </>
  );
};

export default QuickProductForm;
